// ─── src/pages/HomePage.js ────────────────────────────────────────────────
import React from "react";
import { Link } from "react-router-dom";
import { trackClick } from "../telemetry";
import { emitApplyClick } from "../webEvents";

/*  logo lives in src/assets/  */
import Logo from "../assets/composiq-mark-128.png";
// TODO: Replace with final 90-second demo video when available
import DemoVideo from "../assets/demo.mp4";
import { LockSimple, GlobeHemisphereWest, ChatCircleText, CheckCircle, FirstAid, MagnifyingGlass, Eye, ShareNetwork } from "@phosphor-icons/react";
 
const BLOCKED_EMAIL_DOMAINS = [
  'gmail.com','yahoo.com','outlook.com','hotmail.com','icloud.com',
  'aol.com','proton.me','protonmail.com','gmx.com','live.com',
  'msn.com','mail.com','yandex.com','zoho.com'
];

/* ── Custody ledger ──────────────────────────────────────────────────────────
   The ledger is a real hash chain: every row is digested together with the
   hash of the row above it. Seed rows are offsets in seconds from the start of
   the session so the whole register reads as one continuous session that ends
   a few minutes before the visitor arrived.                                   */
const CUSTODY_SEED_ENTRIES = [
  { offset: 0,   op: 'ingest', object: 'contract_0412.pdf' },
  { offset: 3,   op: 'parse',  object: 'contract_0412.pdf' },
  { offset: 51,  op: 'embed',  object: 'chunks 1-48'       },
  { offset: 344, op: 'read',   object: 'user m.ellis'      },
  { offset: 369, op: 'answer', object: '3 citations'       }
];

const AZURE_MARKETPLACE_URL =
  'https://portal.azure.com/#view/Microsoft_Azure_Marketplace/MarketplaceOffersBlade/selectedMenuItemId/home';

/* Rows written live, in front of the visitor, when the hero finishes typing. */
const CUSTODY_LIVE_ENTRIES = [
  { op: 'deploy', object: 'your azure tenant'      },
  { op: 'egress', object: '0 bytes to third party', highlight: true },
  { op: 'seal',   object: 'cited answer for audit' }
];

const CUSTODY_GENESIS_HASH = '00000000';
const CUSTODY_PENDING_HASH = '········';
const CUSTODY_SESSION_TAIL_SECONDS = 180;  // last seed row lands this long before "now"
const CUSTODY_ROW_INTERVAL_MS = 760;
const CUSTODY_DIGEST_DWELL_MS = 280;       // minimum time the "computing" cell stays legible
const CUSTODY_STAMP_DELAY_MS = 420;

const padTwo = (value) => String(value).padStart(2, '0');

const formatClockTime = (date) =>
  `${padTwo(date.getHours())}:${padTwo(date.getMinutes())}:${padTwo(date.getSeconds())}`;

const custodyPayload = (prevHash, entry) =>
  `${prevHash}|${entry.time}|${entry.op}|${entry.object}`;

/* FNV-1a: synchronous, used for first paint and wherever crypto.subtle is
   unavailable (it requires a secure context). */
function fnv1aHex(input) {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
}

async function custodyHash(prevHash, entry) {
  const payload = custodyPayload(prevHash, entry);
  const subtle = typeof window !== 'undefined' && window.crypto && window.crypto.subtle;
  if (!subtle || typeof TextEncoder === 'undefined') return fnv1aHex(payload);
  try {
    const digest = await subtle.digest('SHA-256', new TextEncoder().encode(payload));
    return Array.from(new Uint8Array(digest, 0, 4))
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join('');
  } catch (err) {
    return fnv1aHex(payload);
  }
}

function buildCustodySeedEntries() {
  const span = CUSTODY_SEED_ENTRIES[CUSTODY_SEED_ENTRIES.length - 1].offset;
  const sessionStart = Date.now() - (span + CUSTODY_SESSION_TAIL_SECONDS) * 1000;
  let prevHash = CUSTODY_GENESIS_HASH;
  return CUSTODY_SEED_ENTRIES.map((seed, index) => {
    const entry = {
      id: `seed-${index}`,
      time: formatClockTime(new Date(sessionStart + seed.offset * 1000)),
      op: seed.op,
      object: seed.object
    };
    prevHash = fnv1aHex(custodyPayload(prevHash, entry));
    return { ...entry, hash: prevHash };
  });
}

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  !!window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ── Boundary scene ──────────────────────────────────────────────────────────
   One stage, two scenarios the visitor chooses between. In the public-tool
   scenario slips cross the company edge onto an intake line, are consumed by a
   third-party processing structure, and leave it as undifferentiated marks
   bound for parties the sender cannot enumerate. In the Zerivo scenario the
   same slips turn back at the edge and each completed loop puts a real digest
   in the register. Geometry is in SVG user units against a 640×300 viewBox
   chosen so the whole chain still resolves at phone widths; the flight paths
   are CSS keyframes driven by the per-slip custom properties set below.
   Durations live here so the JS timeline and the CSS cannot drift apart.

   The words are held to what is provable: nothing here names a recipient or
   asserts a transfer. The diagram shows loss of custody; the caption says only
   that retention and downstream use pass out of the sender's control.         */
const BOUNDARY_SLIPS = [
  { id: 'a', object: 'contract_0412' },
  { id: 'b', object: 'fy27_model'    },
  { id: 'c', object: 'policy 7.2'    }
];

const BOUNDARY_SCENARIOS = ['leak', 'hold'];

const BOUNDARY_COPY = {
  leak: {
    tenant: 'YOUR COMPANY',
    caption: 'Public AI tool',
    note: 'Once a document crosses the boundary, retention and downstream use are set by terms you do not write and cannot audit. It becomes training material, and the only record of the exchange sits with the vendor — you have nothing to produce at audit.',
    alt: 'Beyond the boundary: an intake line feeds a third-party processing column, documents come out of it as anonymous marks, the marks pass into a model training block whose output is retained, and fragments of the documents collect inside four other organizations labelled competitor, supplier, stranger and anyone.',
    docsAlt: 'Three documents cross the company boundary and leave through its edge.',
    ledgerTitle: "Vendor's record",
    ledgerOwner: 'Not yours'
  },
  hold: {
    tenant: 'YOUR AZURE TENANT',
    caption: 'Zerivo',
    note: 'The document never crosses the boundary. Each exchange closes inside your tenant and is written to a hash-chained, tamper-evident record you hold, where every digest is computed over the row above it.',
    alt: 'Beyond the boundary: the intake line, the third-party processing column, the model training block and the four other organizations receive nothing.',
    docsAlt: 'Three documents circulate inside the tenant boundary, reaching a four-point star labelled AI model and turning back at it — the model answering them sits inside the boundary with them.',
    ledgerTitle: 'Your record',
    ledgerOwner: 'Hash-chained'
  }
};

/* The shelf the record would sit on, when the record is on someone else's
   premises instead. Stated once, in the register's own grammar. */
const BOUNDARY_VACANCY = {
  title: 'Your record',
  owner: 'None written',
  sr: 'Empty: the exchange left no record inside your tenant.'
};

/* ── The tenant interior ─────────────────────────────────────────────────────
   The boundary is no longer a rectangle drawn inside the diagram; it is the
   container the record physically lives in. Documents animate in this small
   coordinate space, and the register sits underneath them behind the same
   line, so containment is a fact of the layout rather than a resemblance.
   Documents converge on one exit lane before leaving, so the handoff to the
   intake outside reads as a single opening in the wall.                      */
const BOUNDARY_TN_W = 378;
const BOUNDARY_TN_H = 104;
const BOUNDARY_TN_LANE_Y = [6, 40, 74];
const BOUNDARY_TN_EXIT_Y = BOUNDARY_TN_LANE_Y[1];
const BOUNDARY_TN_ORIGIN_X = 20;
const BOUNDARY_TN_TURN_X = 292;
/* Circulating documents turn back sooner than they used to, because there is
   now something there to turn back at. The leak path keeps the original turn as
   its waypoint, so the exit through the wall is unchanged. */
const BOUNDARY_TN_HOLD_TURN_X = 250;
const BOUNDARY_TN_EXIT_X = 336;
const BOUNDARY_TN_GONE_X = 392;
const BOUNDARY_TN_RETURN_DY = 10;
/* The thing that answers, and the reason the documents turn back. Placed in the
   band the lanes stop short of, so it needs no arrows: the slips advance to it
   and reverse there. The label threads the gap between the first and second
   lane — a strip no document ever occupies — which is how it earns its width
   without taking any from the documents or the register. */
const BOUNDARY_TN_AI = { x: 330, y: 58, r: 14 };
const BOUNDARY_TN_AI_SPARK = { x: 352, y: 40, r: 5.5 };
/* Centred on the mark, not anchored to its left — the same call the training
   label makes across the line, and for the same reason: a left-anchored label
   out here reads as floating beside the mark rather than naming it. Baseline
   sits ten units above the star, matching the gap MODEL TRAINING keeps above
   its block, so the two carry equal weight. */
const BOUNDARY_TN_AI_LABEL_Y = 34;
/* Four-point star, concave edges: two control points per edge pulled toward the
   centre at 0.3r. The convention every audience already reads as "AI", drawn
   at the diagram's own hairline weight instead of as a glowing badge. */
const boundaryStarPath = (r) => {
  const c = +(r * 0.3).toFixed(2);
  return `M0 ${-r} C0 ${-c} ${c} 0 ${r} 0 C${c} 0 0 ${c} 0 ${r} `
    + `C0 ${c} ${-c} 0 ${-r} 0 C${-c} 0 0 ${-c} 0 ${-r} Z`;
};

/* Outside the line. The viewBox is cropped to begin past the old boundary
   edge: the flight paths and every keyframe stop are unchanged, so slips
   simply become visible as they arrive on the intake. */
const BOUNDARY_VIEW_X = 212;
const BOUNDARY_VIEW_Y = 14;
const BOUNDARY_VIEW_W = 428;
const BOUNDARY_VIEW_H = 206;
const BOUNDARY_LANE_Y = [64, 104, 144];
const BOUNDARY_ORIGIN_X = 24;
const BOUNDARY_SPLIT_X = 202;   // first user unit clear of the boundary edge
const BOUNDARY_TURN_X = 156;    // where the slip stops when the boundary holds
const BOUNDARY_RETURN_DY = 18;

/* Outside the line: intake → processing → training → distribution. The
   processing column sits further left than the frame strictly needs so the
   training step has room to be labelled between it and the recipients. */
const BOUNDARY_BELT_Y = 104;            // slip top edge while riding the intake
const BOUNDARY_BELT_SURFACE_Y = 124;    // BELT_Y + slip height
const BOUNDARY_BELT_X0 = 214;
const BOUNDARY_BELT_X1 = 322;
const BOUNDARY_MOUTH_X = 306;
const BOUNDARY_PLANT = { x: 322, y: 64, w: 66, h: 120 };
/* Staggered baffles: a separation column in schematic, not a building. */
const BOUNDARY_BAFFLE_W = 46;
const BOUNDARY_BAFFLES = [
  { y: 80,  right: false },
  { y: 100, right: true  },
  { y: 120, right: false },
  { y: 140, right: true  },
  { y: 160, right: false }
];
/* The training step, named rather than illustrated: a block on the outflow
   with stacked rules inside, so the marks visibly enter something that keeps
   them before anything fans out. */
const BOUNDARY_TRAIN = { x: 410, y: 100, w: 78, h: 48 };
const BOUNDARY_TRAIN_MID_X = BOUNDARY_TRAIN.x + BOUNDARY_TRAIN.w / 2;
const BOUNDARY_TRAIN_RULES = [112, 124, 136];
const BOUNDARY_SPINE_X = 504;
/* Unequal widths so the row reads as several distinct parties rather than a
   repeated placeholder — four identical rectangles read as a loading skeleton.
   The labels name a class of beneficiary, never a company: what is claimed is
   that a model trained on the document answers for parties the sender does not
   choose. */
const BOUNDARY_ORGS = [
  { y: 34,  w: 100, label: 'COMPETITOR' },
  { y: 82,  w: 84,  label: 'SUPPLIER'   },
  { y: 130, w: 92,  label: 'STRANGER'   },
  { y: 178, w: 68,  label: 'ANYONE'     }
];
const BOUNDARY_ORG = { x: 522, h: 36 };
const BOUNDARY_ORG_MID = BOUNDARY_ORGS.map((org) => org.y + BOUNDARY_ORG.h / 2);
const BOUNDARY_SETTLED_X = [212, 244, 276];

/* Accumulation: one paper fragment per document consumed, capped by how many
   fit the block. They overlap into a fanned stack rather than sitting in a row
   — spaced evenly they read as a progress meter. The blocks stay solid and
   unreadable: the pile says only that volume collects there over time, never
   what any of it contains or who ends up holding it. */
const BOUNDARY_FRAG_W = 9;
const BOUNDARY_FRAG_PITCH = 6;
const BOUNDARY_FRAG_INSET = 8;
const boundaryOrgCap = (w) =>
  Math.max(0, Math.min(6, Math.floor((w - BOUNDARY_FRAG_INSET * 2 - BOUNDARY_FRAG_W) / BOUNDARY_FRAG_PITCH) + 1));
const BOUNDARY_ORG_FILL_MAX = Math.max(...BOUNDARY_ORGS.map((org) => boundaryOrgCap(org.w)));

const BOUNDARY_LEAK_CYCLE_MS = 5200;
const BOUNDARY_HOLD_CYCLE_MS = 4600;
const BOUNDARY_FLOW_CYCLE_MS = 1500;
const BOUNDARY_CLEAR_MS = 620;
const BOUNDARY_ROW_LIMIT = 5;

const boundaryStagger = (cycle) => Math.round(cycle / BOUNDARY_SLIPS.length);
/* Keyframe stops the JS timeline has to meet: the 30% crossing of
   bscene-cross, the 92% fade-out of the same, and the 96% closing of
   bscene-loop. */
const BOUNDARY_LEAK_CROSS_MS = Math.round(BOUNDARY_LEAK_CYCLE_MS * 0.3);
const BOUNDARY_LEAK_MORPH_MS = Math.round(BOUNDARY_LEAK_CYCLE_MS * 0.92);
const BOUNDARY_LEAK_ABSORB_MS = Math.round(BOUNDARY_LEAK_CYCLE_MS * 0.97);
const BOUNDARY_HOLD_CLOSE_MS = Math.round(BOUNDARY_HOLD_CYCLE_MS * 0.96);
const BOUNDARY_LEAK_BEAT_MS =
  (BOUNDARY_SLIPS.length - 1) * boundaryStagger(BOUNDARY_LEAK_CYCLE_MS) + BOUNDARY_LEAK_CYCLE_MS;

const BOUNDARY_SLIP_PATH =
  'M0 0 H30 V16 L26.25 20 L22.5 16 L18.75 20 L15 16 L11.25 20 L7.5 16 L3.75 20 L0 16 Z';

/* A timer set that can be held and released. The CSS side of the scene is
   paused with animation-play-state, which freezes it in place rather than
   rewinding it; the register has to behave the same way, so every scheduled
   write here banks its remaining time on pause and is re-armed with exactly
   that much left on resume. Without this the diagram would stop while rows
   kept landing, and the two would come back out of step. */
function createBoundaryClock(startPaused) {
  const tasks = new Set();
  let paused = !!startPaused;

  const arm = (task) => {
    task.timer = setTimeout(() => {
      task.timer = null;
      if (task.every) {
        task.due = Date.now() + task.every;
        arm(task);
      } else {
        tasks.delete(task);
      }
      task.fn();
    }, Math.max(0, task.due - Date.now()));
  };

  const add = (delay, every, fn) => {
    const task = { fn, every, due: Date.now() + delay, remaining: delay, timer: null };
    tasks.add(task);
    if (!paused) arm(task);
    return task;
  };

  return {
    after: (delay, fn) => add(delay, null, fn),
    every: (interval, fn) => add(interval, interval, fn),
    cancel(task) {
      if (!task) return;
      if (task.timer) clearTimeout(task.timer);
      task.timer = null;
      tasks.delete(task);
    },
    setPaused(next) {
      if (!!next === paused) return;
      paused = !!next;
      const now = Date.now();
      tasks.forEach((task) => {
        if (paused) {
          if (task.timer) clearTimeout(task.timer);
          task.timer = null;
          task.remaining = Math.max(0, task.due - now);
        } else {
          task.due = now + task.remaining;
          arm(task);
        }
      });
    },
    clear() {
      tasks.forEach((task) => { if (task.timer) clearTimeout(task.timer); });
      tasks.clear();
    }
  };
}

const BoundarySlip = ({ outbound }) => (
  <g className={`bscene-slip${outbound ? ' is-outbound' : ''}`}>
    <path className="bscene-slip-body" d={BOUNDARY_SLIP_PATH} />
    <path className="bscene-slip-line" d="M5 5.5 H21" />
    <path className="bscene-slip-line" d="M5 9 H25" />
    <path className="bscene-slip-line" d="M5 12.5 H17" />
  </g>
);

/* A recipient block: solid, unstroked and opaque on purpose — the contrast
   with the hairline-outlined tenant box is the point. It carries a name and,
   over time, fragments; it never becomes something you can see into. */
const BoundaryOrg = ({ org, marks }) => (
  <g>
    <rect
      className="bscene-org"
      x={BOUNDARY_ORG.x} y={org.y}
      width={org.w} height={BOUNDARY_ORG.h}
    />
    <text className="bscene-org-label" x={BOUNDARY_ORG.x + BOUNDARY_FRAG_INSET} y={org.y + 14}>
      {org.label}
    </text>
    {Array.from({ length: marks }, (_, i) => (
      <g
        className="bscene-org-frag"
        key={`frag-${org.y}-${i}`}
        transform={`translate(${BOUNDARY_ORG.x + BOUNDARY_FRAG_INSET + i * BOUNDARY_FRAG_PITCH} ${org.y + 20})`}
      >
        <rect className="bscene-org-frag-body" width={BOUNDARY_FRAG_W} height="11" />
        <line className="bscene-org-frag-line" x1="2" y1="4" x2="7" y2="4" />
        <line className="bscene-org-frag-line" x1="2" y1="7.5" x2="7" y2="7.5" />
      </g>
    ))}
  </g>
);

/* The structure outside the line. It stays drawn in the Zerivo scenario —
   dimmed and idle — because the machinery does not stop existing; it simply
   stops being fed. */
const BoundaryOutside = ({ dormant, fill = 0 }) => (
  <g className={`bscene-outside${dormant ? ' is-dormant' : ''}`}>
    <line
      className="bscene-belt"
      x1={BOUNDARY_BELT_X0 - 6} y1={BOUNDARY_BELT_SURFACE_Y}
      x2={BOUNDARY_BELT_X1} y2={BOUNDARY_BELT_SURFACE_Y}
    />
    <line
      className="bscene-belt-run"
      x1={BOUNDARY_BELT_X0 - 6} y1={BOUNDARY_BELT_SURFACE_Y + 6}
      x2={BOUNDARY_BELT_X1 - 6} y2={BOUNDARY_BELT_SURFACE_Y + 6}
    />
    <text className="bscene-outside-label" x={BOUNDARY_BELT_X0 - 6} y={BOUNDARY_BELT_SURFACE_Y + 26}>
      INTAKE
    </text>

    <rect
      className="bscene-plant"
      x={BOUNDARY_PLANT.x} y={BOUNDARY_PLANT.y}
      width={BOUNDARY_PLANT.w} height={BOUNDARY_PLANT.h} rx="2"
    />
    {BOUNDARY_BAFFLES.map((baffle) => {
      const x1 = baffle.right
        ? BOUNDARY_PLANT.x + BOUNDARY_PLANT.w - BOUNDARY_BAFFLE_W
        : BOUNDARY_PLANT.x;
      return (
        <line className="bscene-tray" key={baffle.y} x1={x1} y1={baffle.y} x2={x1 + BOUNDARY_BAFFLE_W} y2={baffle.y} />
      );
    })}
    <rect className="bscene-aperture" x={BOUNDARY_PLANT.x - 2} y={BOUNDARY_BELT_Y - 4} width="4" height="28" />
    <rect
      className="bscene-aperture"
      x={BOUNDARY_PLANT.x + BOUNDARY_PLANT.w - 2} y={BOUNDARY_BELT_SURFACE_Y - 8}
      width="4" height="16"
    />
    <text className="bscene-outside-label" x={BOUNDARY_PLANT.x} y={BOUNDARY_PLANT.y - 22}>
      <tspan x={BOUNDARY_PLANT.x}>THIRD-PARTY</tspan>
      <tspan x={BOUNDARY_PLANT.x} dy="1.2em">PROCESSING</tspan>
    </text>

    <line
      className="bscene-bus"
      x1={BOUNDARY_PLANT.x + BOUNDARY_PLANT.w} y1={BOUNDARY_BELT_SURFACE_Y}
      x2={BOUNDARY_TRAIN.x} y2={BOUNDARY_BELT_SURFACE_Y}
    />

    {/* the training step: named, not illustrated */}
    <rect
      className="bscene-train"
      x={BOUNDARY_TRAIN.x} y={BOUNDARY_TRAIN.y}
      width={BOUNDARY_TRAIN.w} height={BOUNDARY_TRAIN.h} rx="2"
    />
    {BOUNDARY_TRAIN_RULES.map((y) => (
      <line
        className="bscene-train-rule" key={`rule-${y}`}
        x1={BOUNDARY_TRAIN.x + 10} y1={y} x2={BOUNDARY_TRAIN.x + BOUNDARY_TRAIN.w - 10} y2={y}
      />
    ))}
    {/* Centred on the block rather than left-anchored like the labels further
        upstream: at this spacing a left-anchored label reads as a continuation
        of THIRD-PARTY PROCESSING instead of naming its own step. */}
    <text
      className="bscene-outside-label bscene-train-label" textAnchor="middle"
      x={BOUNDARY_TRAIN_MID_X} y={BOUNDARY_TRAIN.y - 22}
    >
      <tspan x={BOUNDARY_TRAIN_MID_X}>MODEL</tspan>
      <tspan x={BOUNDARY_TRAIN_MID_X} dy="1.2em">TRAINING</tspan>
    </text>
    <text
      className="bscene-outside-label bscene-train-label" textAnchor="middle"
      x={BOUNDARY_TRAIN_MID_X} y={BOUNDARY_TRAIN.y + BOUNDARY_TRAIN.h + 16}
    >
      RETAINED
    </text>

    <line
      className="bscene-bus"
      x1={BOUNDARY_TRAIN.x + BOUNDARY_TRAIN.w} y1={BOUNDARY_BELT_SURFACE_Y}
      x2={BOUNDARY_SPINE_X} y2={BOUNDARY_BELT_SURFACE_Y}
    />
    <line
      className="bscene-bus"
      x1={BOUNDARY_SPINE_X} y1={BOUNDARY_ORG_MID[0]}
      x2={BOUNDARY_SPINE_X} y2={BOUNDARY_ORG_MID[BOUNDARY_ORG_MID.length - 1]}
    />
    {BOUNDARY_ORG_MID.map((mid) => (
      <line className="bscene-bus" key={`branch-${mid}`} x1={BOUNDARY_SPINE_X} y1={mid} x2={BOUNDARY_ORG.x} y2={mid} />
    ))}
    {BOUNDARY_ORGS.map((org) => (
      <BoundaryOrg key={`org-${org.y}`} org={org} marks={Math.min(fill, boundaryOrgCap(org.w))} />
    ))}
  </g>
);

/* What leaves the structure is no longer a document — the slip's shape is gone
   and only uniform marks travel on. Oxide is confined to these marks. */
const BoundaryOutflow = () => (
  <g className="bscene-outflow">
    <line
      className="bscene-outflow-line"
      x1={BOUNDARY_PLANT.x + BOUNDARY_PLANT.w} y1={BOUNDARY_BELT_SURFACE_Y}
      x2={BOUNDARY_TRAIN.x} y2={BOUNDARY_BELT_SURFACE_Y}
    />
    <line
      className="bscene-outflow-line"
      x1={BOUNDARY_TRAIN.x + BOUNDARY_TRAIN.w} y1={BOUNDARY_BELT_SURFACE_Y}
      x2={BOUNDARY_SPINE_X} y2={BOUNDARY_BELT_SURFACE_Y}
    />
    <line
      className="bscene-outflow-line"
      x1={BOUNDARY_SPINE_X} y1={BOUNDARY_BELT_SURFACE_Y}
      x2={BOUNDARY_SPINE_X} y2={BOUNDARY_ORG_MID[0]}
    />
    <line
      className="bscene-outflow-line"
      x1={BOUNDARY_SPINE_X} y1={BOUNDARY_BELT_SURFACE_Y}
      x2={BOUNDARY_SPINE_X} y2={BOUNDARY_ORG_MID[BOUNDARY_ORG_MID.length - 1]}
    />
    {BOUNDARY_ORG_MID.map((mid) => (
      <line
        className="bscene-outflow-line" key={`flow-${mid}`}
        x1={BOUNDARY_SPINE_X} y1={mid} x2={BOUNDARY_ORG.x} y2={mid}
      />
    ))}
  </g>
);

/* Inside the tenant. Same keyframe vocabulary as the outside flight, resolved
   against the interior's own coordinate space. */
const boundaryTenantVars = (index, cycle, leaking) => ({
  '--bs-x0': `${BOUNDARY_TN_ORIGIN_X}px`,
  '--bs-y0': `${BOUNDARY_TN_LANE_Y[index]}px`,
  '--bs-xt': `${leaking ? BOUNDARY_TN_TURN_X : BOUNDARY_TN_HOLD_TURN_X}px`,
  '--bs-yl': `${BOUNDARY_TN_LANE_Y[index] + BOUNDARY_TN_RETURN_DY}px`,
  '--bs-xe': `${BOUNDARY_TN_EXIT_X}px`,
  '--bs-ye': `${BOUNDARY_TN_EXIT_Y}px`,
  '--bs-xb': `${BOUNDARY_TN_GONE_X}px`,
  animationDelay: `${index * boundaryStagger(cycle)}ms`
});

const boundarySlipVars = (index, cycle) => ({
  '--bs-x0': `${BOUNDARY_ORIGIN_X}px`,
  '--bs-y0': `${BOUNDARY_LANE_Y[index]}px`,
  '--bs-xb': `${BOUNDARY_SPLIT_X}px`,
  '--bs-xt': `${BOUNDARY_TURN_X}px`,
  '--bs-yl': `${BOUNDARY_LANE_Y[index] + BOUNDARY_RETURN_DY}px`,
  '--bs-xi': `${BOUNDARY_BELT_X0}px`,
  '--bs-yi': `${BOUNDARY_BELT_Y}px`,
  '--bs-xm': `${BOUNDARY_MOUTH_X}px`,
  animationDelay: `${index * boundaryStagger(cycle)}ms`
});

/* The mark that replaces the slip at the mouth. Its period is the slip's, so
   the two stay locked; the delay pushes its first frame to the 92% stop of
   bscene-cross, which is where the slip starts fading. The mark fades up over
   exactly that window, so the receipt is seen becoming an anonymous tick
   rather than merely disappearing near one. */
const boundaryTickVars = (index, cycle) => ({
  '--bs-tk0': `${BOUNDARY_MOUTH_X + 26}px`,
  '--bs-tky0': `${BOUNDARY_BELT_Y + 9}px`,
  '--bs-tk1': `${BOUNDARY_PLANT.x + BOUNDARY_PLANT.w + 4}px`,
  '--bs-tky1': `${BOUNDARY_BELT_SURFACE_Y - 1}px`,
  '--bs-tk2': `${BOUNDARY_TRAIN.x + BOUNDARY_TRAIN.w / 2 - 3}px`,
  animationDelay: `${index * boundaryStagger(cycle) + BOUNDARY_LEAK_MORPH_MS}ms`
});

/* Two registers, each belonging to a place. Inside the tenant it needs no
   frame of its own — the enclosure around it is the boundary. On the vendor's
   side it takes the solid material the downstream silhouettes are made of, so
   it reads as one of their objects rather than one of yours. */
const BoundaryLedger = ({ scenario, rows, uid }) => {
  const copy = BOUNDARY_COPY[scenario];
  const vendor = scenario === 'leak';
  const titleId = `boundary-ledger-title-${uid}`;
  return (
    <div className={`boundary-ledger${vendor ? ' is-vendor' : ''}`}>
      <div className="boundary-ledger-head">
        <span className="boundary-ledger-title" id={titleId}>{copy.ledgerTitle}</span>
        <span className={`boundary-ledger-owner${vendor ? ' is-alert' : ''}`}>
          {copy.ledgerOwner}
        </span>
      </div>
      <ol className="boundary-ledger-entries" aria-labelledby={titleId}>
        {rows.map((row) => (
          <li className="boundary-ledger-entry" key={row.id}>
            {/* Seconds split out so the smallest phones can drop them and give
                the width back to the document name. */}
            <span className="boundary-ledger-time">
              {row.time.slice(0, 5)}
              <span className="boundary-ledger-secs">{row.time.slice(5)}</span>
            </span>
            <span className="boundary-ledger-op">{row.op}</span>
            <span className="boundary-ledger-object">{row.object}</span>
            {row.absent ? (
              <span className="boundary-ledger-hash is-withheld">
                <span className="bscene-sr-only">digest withheld from you</span>
              </span>
            ) : (
              <span className={`boundary-ledger-hash${row.hash ? '' : ' is-pending'}`}>
                {row.hash || CUSTODY_PENDING_HASH}
              </span>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
};

/* What the tenant holds when the record is somewhere else. The register's own
   head and rule stay exactly where they sit in the other state, so toggling
   reads as the rows leaving rather than a different panel appearing — and the
   shelf they leave behind is visibly bare. */
const BoundaryVacancy = ({ uid }) => (
  <div className="boundary-ledger is-vacant">
    <div className="boundary-ledger-head">
      <span className="boundary-ledger-title" id={`boundary-ledger-title-${uid}`}>
        {BOUNDARY_VACANCY.title}
      </span>
      <span className="boundary-ledger-owner">{BOUNDARY_VACANCY.owner}</span>
    </div>
    <div className="boundary-ledger-entries">
      <span className="bscene-sr-only">{BOUNDARY_VACANCY.sr}</span>
    </div>
  </div>
);

/* The answering point, and the counterpart to MODEL TRAINING across the line.
   The two share a noun and nothing else: theirs is a container with rules
   stacked inside it and fragments collecting downstream, a thing that keeps
   what it is given. This is a point, not a box — documents arrive, an answer
   leaves, nothing accumulates. Same hairline weight, same muted label type and
   the same label placement as its counterpart, so they read as two fates for
   the same object rather than as a diagram element and a product badge.

   Nothing here is drawn in the public state. The box is labelled YOUR COMPANY
   there, the answering demonstrably happens beyond the boundary, and an unlit
   engine inside the tenant would assert a capability that scenario does not
   have. Toggling shows the mark vanish from the exact spot the documents now
   stream through on their way out — the same argument the emptied register
   makes, in the same place. */
const BoundaryAiModel = ({ cycle, still }) => (
  <g className={`bscene-ai${still ? ' is-still' : ''}`} style={{ '--bs-cycle': `${cycle}ms` }}>
    <text
      className="bscene-ai-label" textAnchor="middle"
      x={BOUNDARY_TN_AI.x} y={BOUNDARY_TN_AI_LABEL_Y}
    >
      AI MODEL
    </text>
    <g transform={`translate(${BOUNDARY_TN_AI.x} ${BOUNDARY_TN_AI.y})`}>
      <path className="bscene-ai-star" d={boundaryStarPath(BOUNDARY_TN_AI.r)} />
      <path className="bscene-ai-core" d={boundaryStarPath(BOUNDARY_TN_AI.r * 0.36)} />
    </g>
    <path
      className="bscene-ai-spark"
      transform={`translate(${BOUNDARY_TN_AI_SPARK.x} ${BOUNDARY_TN_AI_SPARK.y})`}
      d={boundaryStarPath(BOUNDARY_TN_AI_SPARK.r)}
    />
  </g>
);

/* The boundary itself: one enclosure, always whole, around the documents and
   whatever record the tenant holds. */
const BoundaryTenant = ({ scenario, settled, rows, uid }) => {
  const copy = BOUNDARY_COPY[scenario];
  const leaking = scenario === 'leak';
  const cycle = leaking ? BOUNDARY_LEAK_CYCLE_MS : BOUNDARY_HOLD_CYCLE_MS;
  return (
    <div className="bscene-tenant">
      <div className="bscene-tenant-label">{copy.tenant}</div>
      <div className="bscene-tenant-body">
        <div className="bscene-tenant-frame" aria-hidden="true" />

        <svg
          className="bscene-svg bscene-tenant-docs"
          viewBox={`0 0 ${BOUNDARY_TN_W} ${BOUNDARY_TN_H}`}
          preserveAspectRatio="xMidYMid meet"
          role="img"
          aria-label={copy.docsAlt}
        >
          {!leaking && <BoundaryAiModel cycle={cycle} still={settled} />}
          {settled && BOUNDARY_SLIPS.map((slip, index) => (
            <g
              key={slip.id}
              transform={`translate(${leaking ? BOUNDARY_TN_EXIT_X : BOUNDARY_TN_ORIGIN_X} ${leaking ? BOUNDARY_TN_EXIT_Y : BOUNDARY_TN_LANE_Y[index]})`}
              opacity={leaking && index ? 0 : 1}
            >
              <BoundarySlip outbound={leaking} />
            </g>
          ))}
          {!settled && (
            <g className="bscene-flight" style={{ '--bs-cycle': `${cycle}ms` }}>
              {BOUNDARY_SLIPS.map((slip, index) => (
                <React.Fragment key={slip.id}>
                  <g
                    className={`bscene-mover ${leaking ? 'bscene-tn-cross' : 'bscene-loop'}`}
                    style={boundaryTenantVars(index, cycle, leaking)}
                  >
                    <BoundarySlip outbound={leaking} />
                  </g>
                  {leaking && (
                    <g className="bscene-mover bscene-tn-return" style={boundaryTenantVars(index, cycle, leaking)}>
                      <BoundarySlip />
                    </g>
                  )}
                </React.Fragment>
              ))}
            </g>
          )}
        </svg>

        {leaking
          ? <BoundaryVacancy uid={uid} />
          : <BoundaryLedger scenario={scenario} rows={rows} uid={uid} />}
      </div>
    </div>
  );
};

const BoundaryStage = ({ scenario, settled, clearing, fill = 0, rows = [], uid = 'live' }) => {
  const copy = BOUNDARY_COPY[scenario];
  const leaking = scenario === 'leak';
  const cycle = leaking ? BOUNDARY_LEAK_CYCLE_MS : BOUNDARY_HOLD_CYCLE_MS;
  return (
    <div className="boundary-stage">
      <div className="bscene-composition">
      <BoundaryTenant scenario={scenario} settled={settled} rows={rows} uid={uid} />
      {/* The far side of the line, and everything that lives there — including
          the only register of the exchange once a document has crossed. */}
      <div className="bscene-beyond">
      <svg
        className="bscene-svg bscene-chain"
        viewBox={`${BOUNDARY_VIEW_X} ${BOUNDARY_VIEW_Y} ${BOUNDARY_VIEW_W} ${BOUNDARY_VIEW_H}`}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label={copy.alt}
        style={{ '--bs-flow': `${BOUNDARY_FLOW_CYCLE_MS}ms` }}
      >
        <BoundaryOutside dormant={!leaking} fill={leaking ? fill : 0} />
        {leaking && <BoundaryOutflow />}

        {settled && leaking && BOUNDARY_SETTLED_X.map((x, index) => (
          <g key={BOUNDARY_SLIPS[index].id} transform={`translate(${x} ${BOUNDARY_BELT_Y})`}>
            <BoundarySlip outbound />
          </g>
        ))}

        {!settled && leaking && (
          <g
            className={`bscene-flight${clearing ? ' is-clearing' : ''}`}
            style={{ '--bs-cycle': `${cycle}ms` }}
          >
            {BOUNDARY_SLIPS.map((slip, index) => (
              <React.Fragment key={slip.id}>
                <g className="bscene-mover bscene-cross" style={boundarySlipVars(index, cycle)}>
                  <BoundarySlip outbound />
                </g>
                <g className="bscene-mover bscene-return" style={boundarySlipVars(index, cycle)}>
                  <BoundarySlip />
                </g>
                <rect
                  className="bscene-tick" width="6" height="2"
                  style={boundaryTickVars(index, cycle)}
                />
              </React.Fragment>
            ))}
          </g>
        )}
      </svg>
      {leaking && <BoundaryLedger scenario={scenario} rows={rows} uid={uid} />}
      </div>
      </div>
      <div className="bscene-caption">{copy.caption}</div>
      <p className="bscene-note">{copy.note}</p>
    </div>
  );
};

export default function HomePage() {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const heroTitle = 'Give your team a zero-egress AI workspace.';
  const [typedTitle, setTypedTitle] = React.useState('');
  const [typingDone, setTypingDone] = React.useState(false);
  /* ── Contact modal state ── */
  const [contactModalOpen, setContactModalOpen] = React.useState(false);
  const [contactForm, setContactForm] = React.useState({ name: '', email: '', organization: '', message: '' });
  const [contactStatus, setContactStatus] = React.useState('idle'); // idle | sending | sent | error

  /* ── Video modal state ── */
  const [videoModalOpen, setVideoModalOpen] = React.useState(false);

  /* ── Fit check modal state ── */
  const [secPacketOpen, setSecPacketOpen] = React.useState(false);
  const [secPacketTitle, setSecPacketTitle] = React.useState('See if Zerivo fits');
  const [secPacketForm, setSecPacketForm] = React.useState({
    email: '', organization: '',
    role: '', stage: '', timeframe: '', environment: '', constraint: '',
    _hp: '' // honeypot
  });
  const [secPacketError, setSecPacketError] = React.useState('');
  const [secPacketStatus, setSecPacketStatus] = React.useState('idle'); // idle | sending | sent

  const trackDemoClick = React.useCallback((eventName) => {
    try{
      if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        window.gtag('event', eventName, {
          page_location: window.location.href,
          page_path: window.location.pathname,
          page_title: document && document.title ? document.title : 'Home',
        });
      }
    }catch(_){}
  },[]);
  React.useEffect(() => {
    if (!contactModalOpen && !videoModalOpen && !secPacketOpen) return;
    const handleKey = (event) => {
      if (event.key === "Escape") {
        setContactModalOpen(false);
        setVideoModalOpen(false);
        setSecPacketOpen(false);
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [contactModalOpen, videoModalOpen, secPacketOpen]);

  // Listen for footer "See demos" click
  React.useEffect(() => {
    const handleOpenSecPacket = (e) => {
      const title = e.detail?.title || 'Demo';
      setSecPacketTitle(title);
      setSecPacketOpen(true);
    };
    window.addEventListener('open-sec-packet', handleOpenSecPacket);
    return () => window.removeEventListener('open-sec-packet', handleOpenSecPacket);
  }, []);

  // Typewriter effect for hero title — feels like the AI is typing
  React.useEffect(() => {
    if (typeof window === 'undefined') {
      setTypedTitle(heroTitle);
      setTypingDone(true);
      return;
    }
    const prefersReducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      setTypedTitle(heroTitle);
      setTypingDone(true);
      return;
    }
    setTypedTitle('');
    setTypingDone(false);
    const startDelay = 180;
    const typeInterval = 55;
    let rafId = 0;
    let startTime = 0;
    let started = false;
    const tick = (now) => {
      if (!started) {
        startTime = now;
        started = true;
      }
      const elapsed = now - startTime;
      if (elapsed < startDelay) {
        rafId = window.requestAnimationFrame(tick);
        return;
      }
      const visibleCount = Math.min(
        heroTitle.length,
        Math.floor((elapsed - startDelay) / typeInterval)
      );
      setTypedTitle(heroTitle.slice(0, visibleCount));
      if (visibleCount >= heroTitle.length) {
        setTypingDone(true);
        return;
      }
      rafId = window.requestAnimationFrame(tick);
    };
    rafId = window.requestAnimationFrame(tick);
    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
    };
  }, [heroTitle]);

  /* ── Custody ledger: seed rows render instantly, then the chain is
       re-digested with SHA-256 so the whole register is one real chain ── */
  const [custodySeedRows, setCustodySeedRows] = React.useState(buildCustodySeedEntries);
  const [custodyLiveRows, setCustodyLiveRows] = React.useState([]);
  const [custodyStampSealed, setCustodyStampSealed] = React.useState(false);
  const [custodyLedgerInView, setCustodyLedgerInView] = React.useState(false);
  const [custodyAnimated] = React.useState(() => !prefersReducedMotion());
  const custodyLedgerRef = React.useRef(null);
  const custodyChainHeadRef = React.useRef(custodySeedRows[custodySeedRows.length - 1].hash);
  const custodyChainReadyRef = React.useRef(null);
  const custodyWritingRef = React.useRef(false);

  React.useEffect(() => {
    let cancelled = false;
    custodyChainReadyRef.current = (async () => {
      let prevHash = CUSTODY_GENESIS_HASH;
      const rows = [];
      for (const seed of custodySeedRows) {
        // Sequential by design — each row's digest consumes the one before it.
        // eslint-disable-next-line no-await-in-loop
        prevHash = await custodyHash(prevHash, seed);
        rows.push({ ...seed, hash: prevHash });
      }
      if (cancelled) return;
      custodyChainHeadRef.current = prevHash;
      setCustodySeedRows(rows);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    const node = custodyLedgerRef.current;
    if (!node) return;
    if (typeof window === 'undefined' || typeof window.IntersectionObserver !== 'function') {
      setCustodyLedgerInView(true);
      return;
    }
    const observer = new window.IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        setCustodyLedgerInView(true);
        observer.disconnect();
      }
    }, { threshold: 0.35 });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  /* The ledger writes itself once the hero has finished typing and the card is
     actually on screen — whichever happens later. */
  React.useEffect(() => {
    if (custodyWritingRef.current) return;
    if (custodyAnimated && !(typingDone && custodyLedgerInView)) return;
    custodyWritingRef.current = true;

    let cancelled = false;
    let finished = false;
    const timers = [];

    /* If the run is torn down before it seals, hand the sequence back so a
       later mount can write it from the top instead of a partial ledger. */
    const teardown = () => {
      cancelled = true;
      timers.forEach(clearTimeout);
      if (finished) return;
      custodyWritingRef.current = false;
      setCustodyLiveRows([]);
      setCustodyStampSealed(false);
    };

    const buildRow = (index) => ({
      ...CUSTODY_LIVE_ENTRIES[index],
      id: `live-${index}`,
      time: formatClockTime(new Date())
    });

    if (!custodyAnimated) {
      (async () => {
        await custodyChainReadyRef.current;
        const rows = [];
        for (let index = 0; index < CUSTODY_LIVE_ENTRIES.length; index += 1) {
          const row = buildRow(index);
          // eslint-disable-next-line no-await-in-loop
          const hash = await custodyHash(custodyChainHeadRef.current, row);
          custodyChainHeadRef.current = hash;
          rows.push({ ...row, hash });
        }
        if (cancelled) return;
        finished = true;
        setCustodyLiveRows(rows);
        setCustodyStampSealed(true);
      })();
      return teardown;
    }

    const writeRow = async (index) => {
      const row = buildRow(index);
      setCustodyLiveRows((rows) => [...rows, { ...row, hash: null }]);
      await custodyChainReadyRef.current;
      // The digest resolves in under a millisecond; hold the computing cell
      // long enough for a person to see the hash land.
      const [hash] = await Promise.all([
        custodyHash(custodyChainHeadRef.current, row),
        new Promise((resolve) => { timers.push(setTimeout(resolve, CUSTODY_DIGEST_DWELL_MS)); })
      ]);
      if (cancelled) return;
      custodyChainHeadRef.current = hash;
      setCustodyLiveRows((rows) => rows.map((r) => (r.id === row.id ? { ...r, hash } : r)));
    };

    CUSTODY_LIVE_ENTRIES.forEach((_, index) => {
      timers.push(setTimeout(() => { writeRow(index); }, index * CUSTODY_ROW_INTERVAL_MS));
    });
    timers.push(setTimeout(
      () => {
        finished = true;
        setCustodyStampSealed(true);
      },
      (CUSTODY_LIVE_ENTRIES.length - 1) * CUSTODY_ROW_INTERVAL_MS + CUSTODY_DIGEST_DWELL_MS + CUSTODY_STAMP_DELAY_MS
    ));

    return teardown;
  }, [custodyAnimated, typingDone, custodyLedgerInView]);

  const custodyRows = React.useMemo(
    () => [...custodySeedRows, ...custodyLiveRows],
    [custodySeedRows, custodyLiveRows]
  );

  /* ── Boundary scene: auto-plays both scenarios once on entry so a passive
       visitor still gets the story, then hands control to the segmented
       switch. A manual selection ends the automatic progression for good and
       replays the chosen scenario from its first frame. Reduced motion swaps
       between two settled compositions instead of animating, and shows both
       side by side until the visitor picks one. ── */
  const [boundaryPhase, setBoundaryPhase] = React.useState('idle');
  const [boundaryScenario, setBoundaryScenario] = React.useState('leak');
  const [boundaryRequest, setBoundaryRequest] = React.useState(null);
  const [boundaryRun, setBoundaryRun] = React.useState(0);
  const [boundaryRows, setBoundaryRows] = React.useState([]);
  const [boundaryInView, setBoundaryInView] = React.useState(false);
  const [boundaryAnimated] = React.useState(() => !prefersReducedMotion());
  const [boundaryPaused, setBoundaryPaused] = React.useState(false);
  const [boundaryFill, setBoundaryFill] = React.useState(0);
  const boundarySceneRef = React.useRef(null);
  const boundaryChainHeadRef = React.useRef(CUSTODY_GENESIS_HASH);
  const boundaryRowSeqRef = React.useRef(0);
  const boundaryOptionRefs = React.useRef([]);
  const boundaryClockRef = React.useRef(null);
  const boundaryPausedRef = React.useRef(false);
  boundaryPausedRef.current = boundaryPaused;

  /* Held rather than torn down: pausing must not discard the beat in flight,
     because resuming would then restart the scene from its first frame. */
  React.useEffect(() => {
    if (boundaryClockRef.current) boundaryClockRef.current.setPaused(boundaryPaused);
  }, [boundaryPaused]);

  const selectBoundaryScenario = React.useCallback((scenario) => {
    setBoundaryRequest(scenario);
    setBoundaryScenario(scenario);
    setBoundaryRun((run) => run + 1);
  }, []);

  const handleBoundarySwitchKey = React.useCallback((event, index) => {
    const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
    if (!step) return;
    event.preventDefault();
    const next = (index + step + BOUNDARY_SCENARIOS.length) % BOUNDARY_SCENARIOS.length;
    selectBoundaryScenario(BOUNDARY_SCENARIOS[next]);
    const node = boundaryOptionRefs.current[next];
    if (node) node.focus();
  }, [selectBoundaryScenario]);

  React.useEffect(() => {
    const node = boundarySceneRef.current;
    if (!node) return undefined;
    if (typeof window === 'undefined' || typeof window.IntersectionObserver !== 'function') {
      setBoundaryInView(true);
      return undefined;
    }
    // Kept live rather than disconnected on first hit: the scene runs on
    // repeating timers, and they should stop while it is off screen.
    const observer = new window.IntersectionObserver((entries) => {
      entries.forEach((entry) => setBoundaryInView(entry.isIntersecting));
    }, { threshold: 0.3 });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  React.useEffect(() => {
    if (!boundaryInView) return undefined;

    let cancelled = false;
    const clock = createBoundaryClock(boundaryPausedRef.current);
    boundaryClockRef.current = clock;
    const at = (delay, fn) => clock.after(delay, fn);

    const buildRow = (index, beat) => {
      boundaryRowSeqRef.current += 1;
      return {
        id: `${beat}-${BOUNDARY_SLIPS[index].id}-${boundaryRowSeqRef.current}`,
        time: formatClockTime(new Date()),
        op: beat === 'leak' ? 'egress' : 'answer',
        object: BOUNDARY_SLIPS[index].object,
        absent: beat === 'leak',
        hash: null
      };
    };

    const pushRow = (row) => setBoundaryRows((rows) => {
      const next = [...rows, row];
      return next.length > BOUNDARY_ROW_LIMIT ? next.slice(next.length - BOUNDARY_ROW_LIMIT) : next;
    });

    boundaryChainHeadRef.current = CUSTODY_GENESIS_HASH;
    setBoundaryRows([]);
    setBoundaryFill(0);

    if (!boundaryAnimated) {
      (async () => {
        const rows = [];
        if (boundaryRequest !== 'hold') {
          BOUNDARY_SLIPS.forEach((_, index) => rows.push(buildRow(index, 'leak')));
        }
        if (boundaryRequest !== 'leak') {
          for (let index = 0; index < BOUNDARY_SLIPS.length; index += 1) {
            const row = buildRow(index, 'hold');
            // Sequential by design — each digest consumes the one before it.
            // eslint-disable-next-line no-await-in-loop
            const hash = await custodyHash(boundaryChainHeadRef.current, row);
            boundaryChainHeadRef.current = hash;
            rows.push({ ...row, hash });
          }
        }
        if (cancelled) return;
        setBoundaryRows(rows);
        setBoundaryPhase('rest');
      })();
      return () => { cancelled = true; };
    }

    const writeLeakRow = (index) => pushRow(buildRow(index, 'leak'));

    const writeHoldRow = (index) => {
      const row = buildRow(index, 'hold');
      pushRow(row);
      // Hold the pending cell long enough to be read before the digest lands.
      at(CUSTODY_DIGEST_DWELL_MS, async () => {
        const hash = await custodyHash(boundaryChainHeadRef.current, row);
        if (cancelled) return;
        boundaryChainHeadRef.current = hash;
        setBoundaryRows((rows) => rows.map((r) => (r.id === row.id ? { ...r, hash } : r)));
      });
    };

    /* One fragment per document consumed, banked on the beat the slip is
       absorbed at the aperture. Capped, so it reads as accrual rather than
       as a count of anything. */
    const bankFragment = () =>
      setBoundaryFill((marks) => Math.min(marks + 1, BOUNDARY_ORG_FILL_MAX));

    const repeats = [];
    const stopRepeats = () => {
      repeats.forEach((task) => clock.cancel(task));
      repeats.length = 0;
    };

    const runBeat = (beat) => {
      const cycle = beat === 'leak' ? BOUNDARY_LEAK_CYCLE_MS : BOUNDARY_HOLD_CYCLE_MS;
      const mark = beat === 'leak' ? BOUNDARY_LEAK_CROSS_MS : BOUNDARY_HOLD_CLOSE_MS;
      const write = beat === 'leak' ? writeLeakRow : writeHoldRow;
      setBoundaryScenario(beat);
      setBoundaryPhase('run');
      BOUNDARY_SLIPS.forEach((_, index) => {
        at(index * boundaryStagger(cycle) + mark, () => {
          write(index);
          repeats.push(clock.every(cycle, () => write(index)));
        });
        if (beat !== 'leak') return;
        at(index * boundaryStagger(cycle) + BOUNDARY_LEAK_ABSORB_MS, () => {
          bankFragment();
          repeats.push(clock.every(cycle, bankFragment));
        });
      });
    };

    if (boundaryRequest) {
      runBeat(boundaryRequest);
    } else {
      runBeat('leak');
      at(BOUNDARY_LEAK_BEAT_MS, () => {
        stopRepeats();
        setBoundaryPhase('clear');
      });
      at(BOUNDARY_LEAK_BEAT_MS + BOUNDARY_CLEAR_MS, () => {
        boundaryChainHeadRef.current = CUSTODY_GENESIS_HASH;
        setBoundaryRows([]);
        setBoundaryFill(0);
        runBeat('hold');
      });
    }

    return () => {
      cancelled = true;
      clock.clear();
      if (boundaryClockRef.current === clock) boundaryClockRef.current = null;
    };
  }, [boundaryInView, boundaryAnimated, boundaryRequest, boundaryRun]);

  /* ── Quiet scroll-reveal: sections fade-rise in as the visitor reads down.
       Armed only when motion is allowed; otherwise everything renders in its
       final state. Kept separate from the custody ledger's own observer. ── */
  const [revealArmed, setRevealArmed] = React.useState(false);
  const layersRef = React.useRef(null);
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    let raf = 0;
    const update = () => {
      raf = 0;
      if (!layersRef.current) return;
      const rect = layersRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const progress = (windowHeight - rect.top) / windowHeight; 
      layersRef.current.style.setProperty('--scroll-progress', progress.toFixed(3));
      
      // Calculate opacity for layers (1.0 at center, fades out at edges)
      const distFromCenter = Math.abs(1 - progress);
      const opacity = Math.max(0, 1 - distFromCenter * 1.5);
      layersRef.current.style.setProperty('--layers-opacity', opacity.toFixed(3));
    };
    const onScroll = () => { if (!raf) raf = window.requestAnimationFrame(update); };
    window.addEventListener('scroll', onScroll, { passive: true });
    update();
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, []);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    if (prefersReducedMotion() || typeof window.IntersectionObserver !== 'function') return;
    setRevealArmed(true);
  }, []);
  React.useEffect(() => {
    if (!revealArmed) return;
    const nodes = Array.from(
      document.querySelectorAll('.home-page [data-reveal], .home-page [data-reveal-group]')
    );
    const observer = new window.IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.14, rootMargin: '0px 0px -6% 0px' });
    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [revealArmed]);

  /* ── Header gains its hairline once the page is scrolled ── */
  const [headerScrolled, setHeaderScrolled] = React.useState(false);
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    let raf = 0;
    const update = () => { raf = 0; setHeaderScrolled(window.scrollY > 4); };
    const onScroll = () => { if (!raf) raf = window.requestAnimationFrame(update); };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    
    // Enable snap scrolling on html for the homepage
    document.documentElement.classList.add('snap-scrolling');
    
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) window.cancelAnimationFrame(raf);
      document.documentElement.classList.remove('snap-scrolling');
    };
  }, []);

  /* ── Contact form submit ── */
  const handleContactSubmit = React.useCallback(async (e) => {
    e.preventDefault();
    setContactStatus('sending');
    try {
      const API_BASE = window.REACT_APP_DEMO_REQUESTS_API || '';
      const controller = new AbortController();
      const t = setTimeout(() => controller.abort(), 45000);
      const res = await fetch(`${API_BASE}/api/lead`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(contactForm),
        signal: controller.signal
      });
      clearTimeout(t);
      if (res.ok) {
        setContactStatus('sent');
        return;
      }
      throw new Error(`Request failed (${res.status})`);
    } catch (err) {
      // TODO: Wire /api/lead endpoint on backend; falling back to mailto
      const subject = encodeURIComponent('Zerivo fit conversation request');
      const body = encodeURIComponent(
        `Name: ${contactForm.name}\nEmail: ${contactForm.email}\nOrganization: ${contactForm.organization}\n\n${contactForm.message}`
      );
      window.location.href = `mailto:contact@zerivo.ai?subject=${subject}&body=${body}`;
      setContactStatus('sent');
    }
  }, [contactForm]);

  /* ── Blocked consumer email domains ── */
  const isBlockedEmail = React.useCallback((email) => {
    const domain = String(email || '').trim().toLowerCase().split('@')[1];
    return !domain || BLOCKED_EMAIL_DOMAINS.includes(domain);
  }, []);

  /* ── Fit check form submit ── */
  const handleSecPacketSubmit = React.useCallback(async (e) => {
    e.preventDefault();
    setSecPacketError('');

    // Client-side validation: blocked domains
    if (isBlockedEmail(secPacketForm.email)) {
      setSecPacketError('Please use a work email address.');
      return;
    }
    if (!secPacketForm.role) {
      setSecPacketError('Please select what brought you here.');
      return;
    }

    setSecPacketStatus('sending');
    try {
      const API_BASE = window.REACT_APP_DEMO_REQUESTS_API || '';
      const controller = new AbortController();
      const t = setTimeout(() => controller.abort(), 45000);
      const res = await fetch(`${API_BASE}/api/security-packet/request`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          email: secPacketForm.email,
          organization: secPacketForm.organization,
          role: secPacketForm.role,
          stage: secPacketForm.stage,
          timeframe: secPacketForm.timeframe,
          environment: secPacketForm.environment,
          constraint: secPacketForm.constraint,
          source: 'fit-check-modal',
          _hp: secPacketForm._hp
        }),
        signal: controller.signal
      });
      clearTimeout(t);
      if (res.ok) {
        setSecPacketStatus('sent');
        return;
      }
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || `Request failed (${res.status})`);
    } catch (err) {
      setSecPacketStatus('idle');
      setSecPacketError(err.message || 'Something went wrong. Please try again.');
    }
  }, [secPacketForm, isBlockedEmail]);

  const themes = [
    {
      id: 'data-protection',
      pill: 'Data Protection',
      features: [
        {
          title: 'Redaction',
          iconEl: <Eye size={56} weight="duotone" color="#111827" />,
          blurb: 'Automated removal of sensitive text from documents and images, preserving compliance without manual overhead.'
        },
        {
          title: 'Containment',
          iconEl: <MagnifyingGlass size={56} weight="duotone" color="#111827" />,
          blurb: 'Discover and prevent Meta Pixel or third-party tracking risks to ensure airtight regulatory compliance.'
        }
      ]
    },
    {
      id: 'validation',
      pill: 'Integrity & Assurance',
      features: [
        {
          title: 'Validation',
          iconEl: <CheckCircle size={56} weight="duotone" color="#111827" />,
          blurb: 'Detect the presence of signatures in contracts and agreements, ensuring integrity and accelerating review cycles.'
        }
      ]
    },
    {
      id: 'dialogue',
      pill: 'Conversational Intelligence',
      features: [
        {
          title: 'Dialogue',
          iconEl: <ChatCircleText size={56} weight="duotone" color="#111827" />,
          blurb: 'Engage with policies of any size using natural language. Ask situational questions and get instant, cited responses.'
        }
      ]
    },
    {
      id: 'compliance',
      pill: 'Governance',
      features: [
        {
          title: 'Compliance',
          iconEl: <FirstAid size={56} weight="duotone" color="#111827" />,
          blurb: 'HIPAA-ready architecture protects employee and patient data. Every interaction is contained within your tenant.'
        }
      ]
    },
    {
      id: 'orchestration',
      pill: 'Organization',
      features: [
        {
          title: 'Orchestration',
          iconEl: <ShareNetwork size={56} weight="duotone" color="#111827" />,
          blurb: 'Transform unstructured content—like meeting notes or policy manuals—into clean, organized outputs ready for action.'
        }
      ]
    }
  ];

  const [activeThemeId, setActiveThemeId] = React.useState('data-protection');
  const activeTheme = themes.find(t => t.id === activeThemeId);

  const [activeSection, setActiveSection] = React.useState('hero');

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const sections = ['hero', 'benefits', 'boundary-scene', 'capability-layers', 'record-story', 'custody-declaration', 'plans'];
    
    const handleScroll = () => {
      let current = 'hero';
      for (const id of sections) {
        const el = document.getElementById(id);
        if (el) {
          const rect = el.getBoundingClientRect();
          // If the top of the section is above the middle of the screen
          if (rect.top <= window.innerHeight / 2) {
            current = id;
          }
        }
      }
      setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const sectionSequence = [
    { id: 'hero', title: 'Why Security Teams Approve Zerivo', nextId: 'benefits' },
    { id: 'benefits', title: 'Nothing leaves the tenant', nextId: 'boundary-scene' },
    { id: 'boundary-scene', title: 'The AI your team will use', nextId: 'capability-layers' },
    { id: 'capability-layers', title: 'Cited answers', nextId: 'record-story' },
    { id: 'record-story', title: 'Articles of custody', nextId: 'custody-declaration' },
    { id: 'custody-declaration', title: 'Pricing', nextId: 'plans' },
    { id: 'plans', title: null, nextId: null }
  ];

  const currentSectionConfig = sectionSequence.find(s => s.id === activeSection);

  /* Both scenarios settled side by side is the reduced-motion default; picking
     a segment narrows it to the one composition the visitor asked for. */
  const boundaryTwoUp = !boundaryAnimated && !boundaryRequest;
  const boundarySettled = !boundaryAnimated || boundaryPhase === 'idle';
  /* A settled composition has no timeline to accrue against, so it shows the
     end state outright; an animated scene that has not started yet shows none. */
  const boundaryOrgFill = boundaryAnimated
    ? (boundarySettled ? 0 : boundaryFill)
    : BOUNDARY_ORG_FILL_MAX;
  /* Two compositions side by side each own their half of the register, so the
     containment contrast is visible in one glance rather than described. */
  const boundaryRowsFor = (scenario) =>
    (boundaryTwoUp ? boundaryRows.filter((row) => Boolean(row.absent) === (scenario === 'leak')) : boundaryRows);

  const recordStoryItems = [
    {
      id: 'spreadsheets',
      pill: 'Spreadsheets',
      title: 'Ask the workbook the question it was never built to answer',
      body: 'The person who built fy27_model.xlsx left in 2019. It feeds three reports and one budget hearing, and nobody wants to be the one who touches it. Ask across all 48 sheets at once and get back something you can hand a finance director with the cells still attached.',
      ask: 'What happens to the vacancy backlog if attrition holds at 9 percent through FY28?',
      source: ['fy27_model.xlsx', '48 sheets', '214,000 rows', 'read in full'],
      rows: [
        { finding: 'Backlog clears in Q3 2027', ref: 'Summary!D22' },
        { finding: 'One hardcoded rate drives it, not headcount', ref: 'Forecast!F22' },
        { finding: 'No other sheet moves materially', ref: '48 sheets traced' }
      ]
    },
    {
      id: 'documents',
      pill: 'Documents',
      title: 'Find the decision after the person who made it retired',
      body: 'The answer is in a PDF from 2014, in a folder nobody has synced since. Zerivo reads it where it already sits, answers in plain language, and cites the minute, the memo, and the date, so the answer survives review.',
      ask: 'Why does the Ridgeway exception exist, and who approved it?',
      source: ['holdings', '41,802 documents', 'since 2011'],
      rows: [
        { finding: 'Board voted it in after the 2014 audit finding', ref: 'minutes · 2014-06-11' },
        { finding: 'Renewed twice, latest under policy 7.2', ref: 'memo · 2019-03-02' },
        { finding: 'Applies only to filings before 2016', ref: 'guidance · 2021-11-08' }
      ]
    },
    {
      id: 'projects',
      pill: 'Project context',
      title: 'Keep the thread of the work after the team turns over',
      body: 'Spreadsheets, documents, and project context answer together. Call the whole record with a single @ in chat and the reply carries every source it drew from, not a summary of a summary.',
      ask: 'Where did the Ridgeway remediation land, and what is still open?',
      source: ['ridgeway remediation', '3 workstreams', 'opened 2023-08'],
      rows: [
        { finding: 'Two workstreams closed, one still open', ref: 'status · 2026-05-14' },
        { finding: 'The open item is the filing cutoff', ref: 'item 118' },
        { finding: 'Owner changed twice since kickoff', ref: 'log · 2025-01-09' }
      ]
    }
  ];
  const [activeRecordStoryId, setActiveRecordStoryId] = React.useState(recordStoryItems[0].id);
  const activeRecordStoryIndex = recordStoryItems.findIndex((item) => item.id === activeRecordStoryId);
  const activeRecordStory = recordStoryItems[activeRecordStoryIndex] || recordStoryItems[0];



  React.useEffect(() => {
    // Snapping is off below this width, so section jumping must be too.
    const snapActive = () =>
      document.documentElement.classList.contains('snap-scrolling') &&
      window.innerWidth > 700;

    const handleWheel = (e) => {
      if (snapActive()) {
        // Only override if they are doing a single tick/click wheel event
        // Continuous trackpad scrolling should be left to the browser's native snap
          if (Math.abs(e.deltaY) > 10 && Math.abs(e.deltaY) < 150) {
          e.preventDefault();
          const sections = Array.from(document.querySelectorAll('.snap-scrolling body .home-page > section'));
          
          let targetSection = null;
          
          if (e.deltaY > 0) {
            // Scrolling down
            targetSection = sections.find(s => s.getBoundingClientRect().top > 50);
          } else {
            // Scrolling up
            targetSection = sections.slice().reverse().find(s => s.getBoundingClientRect().top < -50);
          }
          
          if (targetSection) {
            targetSection.scrollIntoView({ behavior: 'smooth' });
          }
        }
      }
    };

    const handleKeyDown = (e) => {
      if (snapActive()) {
        if (e.key === 'ArrowDown' || e.key === 'PageDown' || e.key === ' ') {
          e.preventDefault();
          const sections = Array.from(document.querySelectorAll('.snap-scrolling body .home-page > section'));
          const targetSection = sections.find(s => s.getBoundingClientRect().top > 50);
          if (targetSection) targetSection.scrollIntoView({ behavior: 'smooth' });
        } else if (e.key === 'ArrowUp' || e.key === 'PageUp') {
          e.preventDefault();
          const sections = Array.from(document.querySelectorAll('.snap-scrolling body .home-page > section'));
          const targetSection = sections.slice().reverse().find(s => s.getBoundingClientRect().top < -50);
          if (targetSection) targetSection.scrollIntoView({ behavior: 'smooth' });
        }
      }
    };

    window.addEventListener('wheel', handleWheel, { passive: false });
    window.addEventListener('keydown', handleKeyDown, { passive: false });
    return () => {
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <div className={`App home-page${revealArmed ? ' reveal-armed' : ''}`}>
      {/* ───────── HEADER ───────── */}
      <header className={`dbx-header${headerScrolled ? ' is-scrolled' : ''}`} role="banner">
        <div className="dbx-header-inner">
          <Link to="/" className="dbx-brand" aria-label="Zerivo home">
            <img className="dbx-brand-mark" src={Logo} alt="" />
            <div className="dbx-brand-text-group">
              <span className="dbx-brand-text">Zerivo</span>
              <span className="dbx-brand-subtitle">Private AI</span>
            </div>
          </Link>

          <div className="dbx-header-right">
            <nav className="dbx-header-nav" aria-label="Main navigation">
              <Link to="/our-team" onClick={() => trackClick("Our Team", { placement: "header" })}>Our Team</Link>
              <Link to="/contact">Contact</Link>
            </nav>

            <div className="dbx-header-actions">
              <a
                href={AZURE_MARKETPLACE_URL}
                className="dbx-btn header-cta"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  trackDemoClick('view_azure_marketplace');
                  emitApplyClick("Get Zerivo on Azure", "header");
                }}
              >
                Get Zerivo on Azure
              </a>
            </div>
          </div>

        </div>
      </header>

      {mobileOpen && (
        <nav className="dbx-mobile-nav" role="navigation" aria-label="Mobile navigation">
          <button className="dbx-mobile-close" onClick={() => setMobileOpen(false)} aria-label="Close menu">×</button>
          <Link 
            to="/our-team" 
            onClick={() => {
              setMobileOpen(false);
              trackClick("Our Team", { placement: "header" });
            }}
          >
            Our Team
          </Link>
          <Link to="/contact" onClick={() => setMobileOpen(false)}>Contact</Link>
          <Link to="/privacy" onClick={() => setMobileOpen(false)}>Privacy</Link>
          <Link to="/terms" onClick={() => setMobileOpen(false)}>Terms</Link>
        </nav>
      )}
      {mobileOpen && <div className="dbx-mobile-backdrop" onClick={() => setMobileOpen(false)} aria-hidden="true"></div>}

      {/* ───────── HERO ───────── */}
      <section id="hero" className="dbx-hero" aria-label="Hero">
        <div className="dbx-hero-inner">
          <div className="dbx-hero-copy">
            <div className="dbx-hero-eyebrow">PRIVATE AI &middot; RUNS IN YOUR AZURE TENANT</div>
            <h1 className="dbx-hero-title">
              <span className="typewriter-stage">
                <span className="typewriter-placeholder" aria-hidden="true">{heroTitle}</span>
                <span
                  className={`typewriter-text ${typingDone ? 'done' : ''}`}
                  aria-label={heroTitle}
                >
                  {typedTitle}
                </span>
              </span>
            </h1>
            <p className="dbx-hero-subtitle problem-first-subtitle">
              Traditional Data Loss Prevention (DLP) can't see browser inputs, and firewall blocks just push employees to their phones. Deploy an approved, private AI in your Azure tenant that guarantees zero egress.
            </p>

            {/* Proof bullets */}
            <ul className="hero-proof-bullets" aria-label="Security guarantees">
              <li><span className="hero-bullet-check" aria-hidden="true">✓</span>Deploys in YOUR Azure tenant</li>
              <li><span className="hero-bullet-check" aria-hidden="true">✓</span>Zero Egress</li>
              <li><span className="hero-bullet-check" aria-hidden="true">✓</span>Tamper-Evident Audit Trail</li>
              <li><span className="hero-bullet-check" aria-hidden="true">✓</span>HIPAA & FERPA Mapped</li>
            </ul>

            <div className="dbx-hero-ctas">
              <div className="dbx-hero-cta-row">
                <a
                  href={AZURE_MARKETPLACE_URL}
                  className="dbx-btn dbx-btn-sample"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => {
                    trackDemoClick('view_azure_marketplace');
                    emitApplyClick("Get Zerivo on Azure", "hero");
                  }}
                >
                  Get Zerivo on Azure<span className="dbx-btn-arrow" aria-hidden="true">→</span>
                </a>
              </div>
              {/* Hidden until demo video and Marketplace listing are ready
              <button
                type="button"
                className="dbx-btn dbx-btn-secondary"
                disabled
                aria-disabled="true"
              >
                Watch 90-second demo
              </button>
              <a
                href="https://portal.azure.com/#view/Microsoft_Azure_Marketplace/MarketplaceOffersBlade/selectedMenuItemId/home"
                className="hero-tertiary-link"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  trackDemoClick('view_azure_marketplace');
                  trackClick("View in Azure Marketplace", { section: "hero" });
                }}
              >
                View Azure Marketplace listing →
              </a>
              */}
            </div>
          </div>

          <div className="dbx-hero-media" aria-label="Audit ledger animation">
            <div className="custody-ledger" ref={custodyLedgerRef}>
              <ol className="custody-entries" aria-label="Example custody ledger entries" aria-live="polite">
                {custodyRows.map((entry, index) => {
                  const isLive = custodyAnimated && index >= custodySeedRows.length;
                  // The row above the newest one draws the connector down to it.
                  const isLinking =
                    custodyAnimated &&
                    custodyLiveRows.length > 0 &&
                    index === custodyRows.length - 2 &&
                    index >= custodySeedRows.length - 1;
                  return (
                    <li
                      className={`custody-entry${isLive ? ' custody-entry-live' : ''}${isLinking ? ' custody-entry-linking' : ''}`}
                      key={entry.id}
                    >
                      <span className="custody-entry-time">{entry.time}</span>
                      <span className="custody-entry-op">{entry.op}</span>
                      <span className={`custody-entry-object ${entry.highlight ? 'is-highlighted' : ''}`}>{entry.object}</span>
                      {entry.hash ? (
                        <span className="custody-entry-hash">{entry.hash}</span>
                      ) : (
                        <span className="custody-entry-hash custody-entry-hash-pending" aria-hidden="true">
                          {CUSTODY_PENDING_HASH}
                        </span>
                      )}
                    </li>
                  );
                })}
              </ol>
              <div
                className={`custody-stamp${custodyAnimated ? (custodyStampSealed ? ' custody-stamp-sealed' : ' custody-stamp-pending') : ''}`}
              >
                In your tenant
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="hero-handoff" aria-hidden="true">
        <div className="hero-handoff-divider"></div>
      </div>

      {/* ───────── BENEFITS ───────── */}
      <section id="benefits" className="plans-section" aria-labelledby="benefits-title" style={{ paddingTop: '8rem', paddingBottom: '4rem', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
        <div className="plans-inner">
          <div className="plans-header" data-reveal="true" style={{ textAlign: 'center' }}>
            <h2 id="benefits-title" className="plans-title" style={{ marginBottom: '3rem', marginInline: 'auto' }}>
              Why Security Teams Approve Zerivo
            </h2>
          </div>

          <div className="plans-cards" data-reveal-group="true" style={{ alignItems: 'stretch' }}>
            {/* Benefit 1 */}
            <div className="plans-card problem-first-card">
              <div className="plans-card-head">
                <h3 className="problem-first-card-title">Kill Shadow AI at the Source</h3>
              </div>
              <p className="problem-first-card-body">
                Stop fighting firewall bypasses. Provide an approved AI workspace that your employees actually want to use, keeping source code, API keys, and sensitive data out of public browser inputs.
              </p>
            </div>

            {/* Benefit 2 */}
            <div className="plans-card problem-first-card">
              <div className="plans-card-head">
                <h3 className="problem-first-card-title">Zero Egress. Verify It Yourself.</h3>
              </div>
              <p className="problem-first-card-body">
                No external calls, no telemetry, no vendor cloud. Every Azure PaaS service runs on private endpoints inside your subscription. Verify your lockdown independently with a single <code>az network nsg show</code> command.
              </p>
            </div>

            {/* Benefit 3 */}
            <div className="plans-card problem-first-card">
              <div className="plans-card-head">
                <h3 className="problem-first-card-title">Tamper-Evident Audit Trail</h3>
              </div>
              <p className="problem-first-card-body">
                Don't just hope data is safe. Get cryptographic proof of every interaction with SHA-256 hash chains, giving you a tamper-evident record that will survive your next compliance audit.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ───────── BOUNDARY SCENE ───────── */}
      <section
        id="boundary-scene"
        className={`boundary-scene-section${boundaryAnimated ? '' : ' is-static'}${boundaryPaused ? ' is-held' : ''}`}
        ref={boundarySceneRef}
        aria-labelledby="boundary-scene-title"
      >
        <div className="boundary-scene-inner">
          <div className="boundary-scene-header" data-reveal="true">
            <div className="boundary-scene-eyebrow">The boundary</div>
            <h2 id="boundary-scene-title" className="boundary-scene-title">
              Your data never leaves your Azure tenant.
            </h2>
            <p className="boundary-scene-body">
              A public assistant carries the prompt out of your tenant and leaves
              you nothing to audit. Zerivo answers inside the boundary and writes
              each exchange to a hash-chained, tamper-evident record you hold.
            </p>
          </div>

          <div className="bscene-switch-row">
            <span className="bscene-switch-legend" id="bscene-switch-legend">View</span>
            <div className="bscene-switch" role="radiogroup" aria-labelledby="bscene-switch-legend">
              {BOUNDARY_SCENARIOS.map((scenario, index) => {
                const checked = boundaryScenario === scenario;
                return (
                  <button
                    key={scenario}
                    type="button"
                    role="radio"
                    aria-checked={checked}
                    tabIndex={checked ? 0 : -1}
                    ref={(node) => { boundaryOptionRefs.current[index] = node; }}
                    className={`bscene-switch-option${checked ? ' is-active' : ''}`}
                    onClick={() => selectBoundaryScenario(scenario)}
                    onKeyDown={(event) => handleBoundarySwitchKey(event, index)}
                  >
                    {BOUNDARY_COPY[scenario].caption}
                  </button>
                );
              })}
            </div>

            {boundaryAnimated ? (
              <button
                type="button"
                className={`bscene-motion${boundaryPaused ? ' is-held' : ''}`}
                aria-pressed={boundaryPaused}
                onClick={() => setBoundaryPaused((held) => !held)}
              >
                <svg className="bscene-motion-glyph" viewBox="0 0 9 10" aria-hidden="true" focusable="false">
                  {boundaryPaused ? (
                    <path d="M1.5 0.6 L8 5 L1.5 9.4 Z" />
                  ) : (
                    <g>
                      <rect x="1.5" y="0.6" width="2.2" height="8.8" />
                      <rect x="5.3" y="0.6" width="2.2" height="8.8" />
                    </g>
                  )}
                </svg>
                <span className="bscene-motion-label">{boundaryPaused ? 'Play' : 'Pause'}</span>
              </button>
            ) : (
              <span className="bscene-motion is-off">
                <span className="bscene-motion-label">Motion off</span>
              </span>
            )}
          </div>

          <div className="boundary-scene-grid">
            {boundaryTwoUp ? (
              <div className="boundary-scene-static">
                {BOUNDARY_SCENARIOS.map((scenario) => (
                  <BoundaryStage
                    key={scenario}
                    uid={scenario}
                    scenario={scenario}
                    settled
                    fill={boundaryOrgFill}
                    rows={boundaryRowsFor(scenario)}
                  />
                ))}
              </div>
            ) : (
              <BoundaryStage
                key={`${boundaryScenario}-${boundaryRun}`}
                scenario={boundaryScenario}
                settled={boundarySettled}
                clearing={boundaryPhase === 'clear'}
                fill={boundaryOrgFill}
                rows={boundaryRows}
              />
            )}
          </div>
        </div>
      </section>

      {/* ───────── CAPABILITY LAYERS ───────── */}
      <section id="capability-layers" className="capability-layers-section" ref={layersRef} aria-labelledby="capability-layers-title">
        <div className="capability-layers-inner">
          <div className="capability-layers-copy" data-reveal="true">
            <div className="capability-layers-eyebrow">The AI your team will actually use</div>
            <h2 id="capability-layers-title" className="capability-layers-title">
              Ask the workbook the question it was never built to answer.
            </h2>
            <p className="capability-layers-body">
              Upload your 48-sheet financial models and get answers with the cells still attached. Give your team the cutting-edge AI capabilities they crave—all while the data never leaves your Azure subscription.
            </p>
          </div>
          <div className="layers-3d-container">
            <div className="layer-3d layer-spreadsheets">
              <div className="layer-plane">
                <div className="mock-spreadsheet">
                  <div className="mock-row mock-header">
                    <div className="mock-cell mock-corner"></div>
                    <div className="mock-cell">A</div>
                    <div className="mock-cell">B</div>
                    <div className="mock-cell">C</div>
                    <div className="mock-cell">D</div>
                  </div>
                  <div className="mock-row">
                    <div className="mock-cell mock-row-num">1</div>
                    <div className="mock-cell"></div>
                    <div className="mock-cell mock-filled-light"></div>
                    <div className="mock-cell"></div>
                    <div className="mock-cell"></div>
                  </div>
                  <div className="mock-row">
                    <div className="mock-cell mock-row-num">2</div>
                    <div className="mock-cell mock-filled"></div>
                    <div className="mock-cell mock-filled"></div>
                    <div className="mock-cell"></div>
                    <div className="mock-cell mock-filled-light"></div>
                  </div>
                  <div className="mock-row">
                    <div className="mock-cell mock-row-num">3</div>
                    <div className="mock-cell"></div>
                    <div className="mock-cell"></div>
                    <div className="mock-cell mock-filled"></div>
                    <div className="mock-cell"></div>
                  </div>
                  <div className="mock-row">
                    <div className="mock-cell mock-row-num">4</div>
                    <div className="mock-cell mock-filled-light"></div>
                    <div className="mock-cell"></div>
                    <div className="mock-cell mock-filled"></div>
                    <div className="mock-cell"></div>
                  </div>
                </div>
              </div>
            </div>
            <div className="layer-3d layer-documents">
              <div className="layer-plane">
                <div className="mock-document">
                  <div className="mock-title"></div>
                  <div className="mock-lines">
                    <div className="mock-line"></div>
                    <div className="mock-line"></div>
                    <div className="mock-line"></div>
                    <div className="mock-line mock-short"></div>
                  </div>
                  <div className="mock-subtitle"></div>
                  <div className="mock-lines">
                    <div className="mock-line"></div>
                    <div className="mock-line"></div>
                    <div className="mock-line mock-short"></div>
                  </div>
                </div>
              </div>
            </div>
            <div className="layer-3d layer-projects">
              <div className="layer-plane">
                <div className="mock-kanban">
                  <div className="mock-k-col">
                    <div className="mock-k-header"></div>
                    <div className="mock-k-card"></div>
                    <div className="mock-k-card"></div>
                  </div>
                  <div className="mock-k-col">
                    <div className="mock-k-header"></div>
                    <div className="mock-k-card"></div>
                  </div>
                  <div className="mock-k-col">
                    <div className="mock-k-header"></div>
                    <div className="mock-k-card"></div>
                    <div className="mock-k-card"></div>
                    <div className="mock-k-card"></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Labels live outside the rotated planes so they read as flat screen text */}
            <div className="layer-labels">
              <span className="layer-label layer-label-spreadsheets">Spreadsheets</span>
              <span className="layer-label layer-label-documents">Documents</span>
              <span className="layer-label layer-label-projects">Projects</span>
            </div>
          </div>
        </div>
      </section>
      {/* ───────── RECORD STORY (tabbed proof) ───────── */}
      <section id="record-story" className="record-story" aria-labelledby="record-story-title">
        <div className="record-story-inner">
          <div className="record-story-header" data-reveal="true">
            <div className="record-story-eyebrow">Cited answers</div>
            <h2 id="record-story-title" className="record-story-title">
              Turn your existing files into answers you can verify.
            </h2>
            <p className="record-story-subtitle">
              Ask across your workbooks, documents, and project history. Every
              answer comes back with the cell, the page, and the date
              behind it, and nothing leaves your tenant.
            </p>
            <div className="record-story-filters" role="tablist" aria-label="What Zerivo answers">
              {recordStoryItems.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  role="tab"
                  aria-selected={activeRecordStory.id === item.id}
                  aria-controls="record-story-panel"
                  className={`record-story-filter ${activeRecordStory.id === item.id ? 'active' : ''}`}
                  onClick={() => setActiveRecordStoryId(item.id)}
                >
                  {item.pill}
                </button>
              ))}
            </div>
          </div>

          <div className="record-story-list" data-reveal="true">
            <article id="record-story-panel" className="record-story-card" key={activeRecordStory.id} role="tabpanel">
              <div className="record-story-copy">
                <div className="record-story-step">0{activeRecordStoryIndex + 1 || 1}</div>
                <div className="record-story-card-eyebrow">{activeRecordStory.pill}</div>
                <h3 className="record-story-card-title">{activeRecordStory.title}</h3>
                <p className="record-story-card-body">{activeRecordStory.body}</p>
              </div>

              <div className="record-story-evidence">
                <div className="record-story-ask">{activeRecordStory.ask}</div>
                <div className="record-story-ledger">
                  <div className="record-story-source">
                    {activeRecordStory.source.map((part, i) => (
                      <React.Fragment key={part}>
                        {i > 0 && <span className="record-story-dot">·</span>}
                        {part}
                      </React.Fragment>
                    ))}
                  </div>
                  {activeRecordStory.rows.map((row) => (
                    <div className="record-story-row" key={row.ref}>
                      <span>{row.finding}</span>
                      <span className="record-story-ref">{row.ref}</span>
                    </div>
                  ))}
                </div>
                <div className="record-story-stamp">Held in your tenant</div>
              </div>
            </article>
          </div>
        </div>
      </section>


      {/* ───────── ARTICLES OF CUSTODY ───────── */}
      <section
        id="custody-declaration"
        className="model-choice-section custody-declaration-section"
        aria-labelledby="custody-declaration-title"
      >
        <div className="model-choice-inner custody-declaration-inner">
          <article className="custody-declaration-card" data-reveal="true">
            <div className="custody-declaration-copy">
              <div>
                <div className="custody-declaration-eyebrow">Declaration</div>
                <h2 id="custody-declaration-title" className="custody-declaration-title">
                  Articles of custody.
                </h2>
                <p className="custody-declaration-intro">
                  Five things an institution should be able to require of any
                  AI system that reads its documents.
                </p>
              </div>

              <div className="custody-declaration-signoff">
                <h3>Ask every AI vendor to sign it.</h3>
                <p>Zerivo signs all five. AI that reads your documents inside your own Azure tenant.</p>
              </div>
            </div>

            <div className="custody-declaration-articles">
              <ol className="custody-declaration-list">
                <li>
                  <span className="custody-declaration-number">I</span>
                  <span>Records stay in the institution's own environment.</span>
                </li>
                <li>
                  <span className="custody-declaration-number">II</span>
                  <span>The model is replaceable without moving a document.</span>
                </li>
                <li>
                  <span className="custody-declaration-number">III</span>
                  <span>Every answer carries the source it came from.</span>
                </li>
                <li>
                  <span className="custody-declaration-number">IV</span>
                  <span>Every access is written to a record the institution holds.</span>
                </li>
                <li>
                  <span className="custody-declaration-number">V</span>
                  <span>Leaving is possible, and documented before you arrive.</span>
                </li>
              </ol>

              <div className="custody-declaration-seal" aria-label="Signed declaration">
                <span>Signed</span>
              </div>
            </div>
          </article>
        </div>
      </section>

      {/* ───────── PRICING ───────── */}
      <section id="plans" className="plans-section" aria-labelledby="plans-title">
        <div className="plans-inner">
          <div className="plans-header" data-reveal="true">
            <div className="plans-eyebrow">PRICING</div>
            <div className="plans-header-row">
              <h2 id="plans-title" className="plans-title">
                Three ways to run Zerivo.
              </h2>
            </div>
          </div>

          <div className="plans-cards" data-reveal-group="true">
            {/* Team */}
            <div className="plans-card">
              <div className="plans-card-head">
                <div className="plans-tier-name">TEAM</div>
                <div className="plans-price">$999<span>/mo.</span></div>
                <div className="plans-desc">One department getting off unapproved tools.</div>
              </div>
              <ul className="plans-features">
                <li><span className="plans-check" aria-hidden="true">✓</span>Up to 25 users</li>
                <li><span className="plans-check" aria-hidden="true">✓</span>Private Azure deployment your team controls</li>
                <li><span className="plans-check" aria-hidden="true">✓</span>Hash-chained audit ledger you can export</li>
                <li><span className="plans-check" aria-hidden="true">✓</span>Document and workbook answers with citations</li>
              </ul>
              <div className="plans-card-action">
                <a
                  href={AZURE_MARKETPLACE_URL}
                  className="dbx-btn plans-btn-secondary"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackClick("View in Azure Marketplace", { section: "plans", tier: "team" })}
                >
                  View in Azure Marketplace
                </a>
              </div>
            </div>

            {/* Pro */}
            <div className="plans-card">
              <div className="plans-card-head">
                <div className="plans-tier-name">PRO</div>
                <div className="plans-price">$2,999<span>/mo.</span></div>
                <div className="plans-desc">An organization-wide rollout with compliance in the room.</div>
              </div>
              <ul className="plans-features">
                <li><span className="plans-check" aria-hidden="true">✓</span>Up to 100 users</li>
                <li><span className="plans-check" aria-hidden="true">✓</span>Everything in Team</li>
                <li><span className="plans-check" aria-hidden="true">✓</span>RBAC, SSO, and retention policy control</li>
                <li><span className="plans-check" aria-hidden="true">✓</span>Named support engineer and deployment review</li>
              </ul>
              <div className="plans-card-action">
                <a
                  href={AZURE_MARKETPLACE_URL}
                  className="dbx-btn plans-btn-primary"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackClick("View in Azure Marketplace", { section: "plans", tier: "pro" })}
                >
                  View in Azure Marketplace
                </a>
              </div>
            </div>

            {/* Enterprise */}
            <div className="plans-card">
              <div className="plans-card-head">
                <div className="plans-tier-name">ENTERPRISE</div>
                <div className="plans-price">Talk to us</div>
                <div className="plans-desc">Multi-tenant, multi-agency, or an agent built to your workflow.</div>
              </div>
              <ul className="plans-features">
                <li><span className="plans-check" aria-hidden="true">✓</span>Unlimited users</li>
                <li><span className="plans-check" aria-hidden="true">✓</span>Custom agents built inside your tenant</li>
                <li><span className="plans-check" aria-hidden="true">✓</span>HIPAA, FERPA, StateRAMP and NIST mapping support</li>
                <li><span className="plans-check" aria-hidden="true">✓</span>Azure Marketplace procurement path</li>
              </ul>
              <div className="plans-card-action">
                <Link
                  to="/contact"
                  className="dbx-btn plans-btn-secondary"
                  onClick={() => trackClick("Talk to sales", { section: "plans", tier: "enterprise" })}
                >
                  Talk to sales
                </Link>
              </div>
            </div>
          </div>

          <div className="plans-footer-note" data-reveal="true">
            Azure compute and storage are billed by Microsoft in your own subscription.
          </div>
        </div>
      </section>








      {/* ───────── CONTACT MODAL ───────── */}
      {contactModalOpen && (
        <div
          className="lightbox-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="See if Zerivo fits"
          onClick={() => setContactModalOpen(false)}
        >
          <div className="contact-modal" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="lightbox-close"
              onClick={() => setContactModalOpen(false)}
              aria-label="Close form"
            >
              ×
            </button>
            {contactStatus === 'sent' ? (
              <div className="contact-modal-success">
                <CheckCircle size={48} weight="duotone" color="#669999" />
                <h3>Thank you</h3>
                <p>We'll reach out within one business day.</p>
                <button type="button" className="dbx-btn dbx-btn-primary" onClick={() => { setContactModalOpen(false); setContactStatus('idle'); setContactForm({ name: '', email: '', organization: '', message: '' }); }}>
                  Close
                </button>
              </div>
            ) : (
              <>
                <h3 className="contact-modal-title">See if Zerivo fits</h3>
                <p className="contact-modal-sub">We'll talk through your environment, review process, and first use case.</p>
                <form onSubmit={handleContactSubmit} className="contact-modal-form">
                  <label className="contact-field">
                    <span>Name <abbr title="required">*</abbr></span>
                    <input
                      type="text"
                      required
                      value={contactForm.name}
                      onChange={(e) => setContactForm(f => ({ ...f, name: e.target.value }))}
                    />
                  </label>
                  <label className="contact-field">
                    <span>Work email <abbr title="required">*</abbr></span>
                    <input
                      type="email"
                      required
                      value={contactForm.email}
                      onChange={(e) => setContactForm(f => ({ ...f, email: e.target.value }))}
                    />
                  </label>
                  <label className="contact-field">
                    <span>Organization</span>
                    <input
                      type="text"
                      value={contactForm.organization}
                      onChange={(e) => setContactForm(f => ({ ...f, organization: e.target.value }))}
                    />
                  </label>
                  <label className="contact-field">
                    <span>Message</span>
                    <textarea
                      rows={3}
                      value={contactForm.message}
                      onChange={(e) => setContactForm(f => ({ ...f, message: e.target.value }))}
                    />
                  </label>
                  <button type="submit" className="dbx-btn dbx-btn-primary contact-submit" disabled={contactStatus === 'sending'}>
                    {contactStatus === 'sending' ? 'Sending…' : 'Submit'}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      {/* ───────── FIT CHECK MODAL ───────── */}
      {secPacketOpen && (
        <div
          className="lightbox-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="See if Zerivo fits"
          onClick={() => setSecPacketOpen(false)}
        >
          <div className="contact-modal sec-packet-modal" onClick={(e) => e.stopPropagation()}>
            {secPacketStatus === 'sent' ? (
              <div className="contact-modal-success">
                <CheckCircle size={48} weight="duotone" color="#669999" />
                {secPacketTitle === 'Book a 15-minute security review' ? (
                  <>
                    <h3>Thank you</h3>
                    <p>A member of our team will reach out within one business day to talk through fit.</p>
                  </>
                ) : (
                  <>
                    <h3>Submitted</h3>
                    <p>We'll reach out within one business day to talk through fit.</p>
                    <Link to="/contact?intent=fit" className="dbx-btn dbx-btn-primary" style={{ marginTop: 12, textAlign: 'center' }} onClick={() => { setSecPacketOpen(false); setSecPacketStatus('idle'); setSecPacketForm({ email: '', organization: '', role: '', stage: '', timeframe: '', environment: '', constraint: '', _hp: '' }); setSecPacketError(''); setSecPacketTitle('See if Zerivo fits'); }}>
                      Share more context
                    </Link>
                  </>
                )}
                <button type="button" className="dbx-btn dbx-btn-ghost" style={{ marginTop: 8 }} onClick={() => { setSecPacketOpen(false); setSecPacketStatus('idle'); setSecPacketForm({ email: '', organization: '', role: '', stage: '', timeframe: '', environment: '', constraint: '', _hp: '' }); setSecPacketError(''); setSecPacketTitle('See if Zerivo fits'); }}>
                  Close
                </button>
              </div>
            ) : (
              <>
                <h3 className="contact-modal-title" style={{ marginBottom: 20 }}>{secPacketTitle}</h3>

                {secPacketError && (
                  <div className="sec-packet-error" role="alert">{secPacketError}</div>
                )}

                <form onSubmit={handleSecPacketSubmit} className="contact-modal-form">
                  {/* Honeypot — visually hidden, bots fill it */}
                  <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }}>
                    <label>
                      <span>Leave blank</span>
                      <input
                        type="text"
                        tabIndex={-1}
                        autoComplete="off"
                        value={secPacketForm._hp}
                        onChange={(e) => setSecPacketForm(f => ({ ...f, _hp: e.target.value }))}
                      />
                    </label>
                  </div>

                  <label className="contact-field">
                    <span>Work email <abbr title="required">*</abbr></span>
                    <input
                      type="email"
                      required
                      autoFocus
                      value={secPacketForm.email}
                      onChange={(e) => { setSecPacketForm(f => ({ ...f, email: e.target.value })); setSecPacketError(''); }}
                    />
                  </label>
                  <label className="contact-field">
                    <span>Organization <abbr title="required">*</abbr></span>
                    <input
                      type="text"
                      required
                      value={secPacketForm.organization}
                      onChange={(e) => setSecPacketForm(f => ({ ...f, organization: e.target.value }))}
                    />
                  </label>

                  <fieldset className="sec-packet-fieldset">
                    <legend>What brought you here? <abbr title="required">*</abbr></legend>
                    <div className="sec-packet-radio-group">
                      {['Evaluating private AI options','Need to satisfy internal review','Shadow AI concerns','Board or leadership mandate','Just exploring'].map(v => (
                        <label key={v} className="sec-packet-radio">
                          <input type="radio" name="sp-role" value={v} checked={secPacketForm.role === v} onChange={() => setSecPacketForm(f => ({ ...f, role: v }))} />
                          <span>{v}</span>
                        </label>
                      ))}
                    </div>
                  </fieldset>

                  <button type="submit" className="dbx-btn dbx-btn-primary contact-submit" disabled={secPacketStatus === 'sending'}>
                    {secPacketStatus === 'sending' ? 'Submitting…' : 'Submit'}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      {/* ───────── VIDEO MODAL ───────── */}
      {videoModalOpen && (
        <div
          className="lightbox-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Demo video"
          onClick={() => setVideoModalOpen(false)}
        >
          <div className="video-modal" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="lightbox-close"
              onClick={() => setVideoModalOpen(false)}
              aria-label="Close video"
            >
              ×
            </button>
            {/* TODO: Replace with final 90-second product demo video */}
            <video
              className="video-modal-player"
              src={DemoVideo}
              controls
              autoPlay
              playsInline
            >
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      )}

      

        {/* ───────── COMPARE OPTIONS ───────── */}
        <section className="compare-options" aria-labelledby="compare-title" style={{display: 'none'}}>
          <h3 id="compare-title" className="compare-title">Compare Options</h3>
          <div className="compare-card">
            <div className="compare-scroll" role="region" aria-label="Pro vs Enterprise feature comparison">
              <table className="compare-table">
                <thead>
                  <tr>
                    <th scope="col" className="col-feature">Feature</th>
                    <th scope="col" className="col-plan">Pro</th>
                    <th scope="col" className="col-plan">Enterprise</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th scope="row">Zero-egress architecture</th>
                    <td className="center"><span className="tick" role="img" aria-label="Included">✓</span></td>
                    <td className="center"><span className="tick" role="img" aria-label="Included">✓</span></td>
                  </tr>
                  <tr>
                    <th scope="row">Tamper-evident audit & tracing</th>
                    <td className="center"><span className="tick" role="img" aria-label="Included">✓</span></td>
                    <td className="center"><span className="tick" role="img" aria-label="Included">✓</span></td>
                  </tr>
                  <tr>
                    <th scope="row">Private data, never used for training</th>
                    <td className="center"><span className="tick" role="img" aria-label="Included">✓</span></td>
                    <td className="center"><span className="tick" role="img" aria-label="Included">✓</span></td>
                  </tr>
                  <tr>
                    <th scope="row">GPT-5 access</th>
                    <td className="center"><span className="tick" role="img" aria-label="Included">✓</span></td>
                    <td className="center"><span className="tick" role="img" aria-label="Included">✓</span></td>
                  </tr>
                  <tr>
                    <th scope="row">Multi-model options</th>
                    <td className="center"><span className="dash" role="img" aria-label="Not applicable">—</span><sup className="note-ref">1</sup></td>
                    <td className="center"><span className="tick" role="img" aria-label="Included">✓</span></td>
                  </tr>
                  <tr>
                    <th scope="row">Customer-controlled compute</th>
                    <td className="center"><span className="dash" role="img" aria-label="Not applicable">－</span></td>
                    <td className="center"><span className="tick" role="img" aria-label="Included">✓</span></td>
                  </tr>
                  <tr>
                    <th scope="row">Customer-managed keys (BYOK/CMK)</th>
                    <td className="center"><span className="tick" role="img" aria-label="Optional">✓</span><span className="opt-note"> optional</span><sup className="note-ref">2</sup></td>
                    <td className="center"><span className="tick" role="img" aria-label="Included">✓</span></td>
                  </tr>
                  <tr>
                    <th scope="row">Advanced network configuration</th>
                    <td className="center"><span className="dash" role="img" aria-label="Not applicable">－</span></td>
                    <td className="center"><span className="tick" role="img" aria-label="Included">✓</span></td>
                  </tr>
                  <tr>
                    <th scope="row">High-performance tuning</th>
                    <td className="center"><span className="dash" role="img" aria-label="Not applicable">－</span></td>
                    <td className="center"><span className="tick" role="img" aria-label="Included">✓</span></td>
                  </tr>
                  <tr>
                    <th scope="row">1-day deployment</th>
                    <td className="center"><span className="tick" role="img" aria-label="Included">✓</span></td>
                    <td className="center"><span className="dash" role="img" aria-label="Not applicable">－</span></td>
                  </tr>
                  <tr>
                    <th scope="row">Architect-led setup</th>
                    <td className="center"><span className="tick" role="img" aria-label="Optional">✓</span><span className="opt-note"> optional</span></td>
                    <td className="center"><span className="tick" role="img" aria-label="Included">✓</span></td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="compare-legend" role="note">
              <div>✓ = Included</div>
              <div>✓ optional = Available on request</div>
              <div>— = Not applicable</div>
            </div>
            <div className="compare-notes" role="note" aria-label="Comparison notes">
              <div className="notes-title">Notes</div>
              <ol>
                <li>Pro provides access to vetted Azure OpenAI models available in your tenant. Enterprise extends this to include additional vetted open-source and custom models.</li>
                <li>Customer-Managed Keys (CMK) support for Azure OpenAI is region-dependent; available where Microsoft enables CMK encryption-at-rest for deployed models.</li>
              </ol>
            </div>
          </div>
        </section>

      {/* ───────── KEY VALUE POINTS ───────── */}
      <section className="features" id="features" style={{display: 'none'}}>
        <div className="feature-card teal-mid-slight">
          <h3 style={{display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '8px'}}>
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              background: 'rgba(102, 153, 153, 0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px solid rgba(102, 153, 153, 0.40)'
            }}>
              <LockSimple size={32} weight="duotone" color="#669999" />
            </div>
            <span>Private by Design</span>
          </h3>
          <p style={{textAlign: 'center', fontSize: '1.1rem'}}>
            Your data never leaves your environment.
          </p>
        </div>

        <div className="feature-card teal-dark">
          <h3 style={{display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '8px'}}>
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              background: 'rgba(102, 153, 153, 0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px solid rgba(102, 153, 153, 0.40)'
            }}>
              <GlobeHemisphereWest size={32} weight="duotone" color="#669999" />
            </div>
            <span>Tenant-Controlled</span>
          </h3>
          <p>
            Each organization runs in its own secure tenant. Your AI is yours alone.
          </p>
        </div>
      </section>

      {/* ───────── THEME PILLS ───────── */}
      <section className="theme-pills" aria-labelledby="themes-title" style={{display: 'none'}}>
        <h3 id="themes-title" className="themes-title">Features</h3>
        <div className="pill-row" role="tablist" aria-label="feature themes">
          {themes.map(t => (
            <button
              key={t.id}
              role="tab"
              aria-selected={activeThemeId === t.id}
              className={`pill ${activeThemeId === t.id ? 'active' : ''}`}
              onClick={() => setActiveThemeId(t.id)}
            >
              {t.pill}
            </button>
          ))}
        </div>

        <div className="theme-detail" role="tabpanel" aria-live="polite">
          {activeTheme.features.map((f, idx) => (
            <div className="feature-row" key={idx}>
              <div className="detail-icon" aria-hidden="true">{f.iconEl}</div>
              <div className="detail-text">
                <h4 className="detail-title">{f.title}</h4>
                <p className="detail-blurb">{f.blurb}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {currentSectionConfig && currentSectionConfig.nextId && (
        <button
          className="skip-to-next-btn"
          onClick={() => {
            const el = document.getElementById(currentSectionConfig.nextId);
            if (el) {
              el.scrollIntoView({ behavior: 'smooth' });
            }
          }}
          aria-label={`Skip to ${currentSectionConfig.title}`}
        >
          Next — {currentSectionConfig.title}
          <span className="skip-arrow" aria-hidden="true">↓</span>
        </button>
      )}

    </div>
  );
}
